import { ASSIST, START_LIVES } from '../data/tuning';
import { SaveOperationError } from './errors';
import { SLOT_IDS, type SaveFile, type SaveSlot, type SlotId } from './schema';

/** Slot operations of GDD §6.3: pure functions that return a new document and never mutate the input. */

export function isSlotId(value: unknown): value is SlotId {
  return (SLOT_IDS as readonly unknown[]).includes(value);
}

function assertSlotId(value: unknown): asserts value is SlotId {
  if (!isSlotId(value)) throw new SaveOperationError('invalid slot id');
}

/** A slot at the start of the game: level 1-1 from the beginning, starting lives, everything else at zero. */
export function emptySlot(now: string, lives: number = START_LIVES): SaveSlot {
  return {
    createdAt: now,
    updatedAt: now,
    playTimeMs: 0,
    unlockedLevels: ['1-1'],
    completedLevels: {},
    current: { levelId: '1-1', checkpointId: null, lives, dollars: 0, score: 0, scoreAtLevelStart: 0, cleanRun: true },
    stats: { deaths: 0, flaps: 0, bossesDefeated: 0, perfectJumps: 0 },
  };
}

/** New game in a slot; it becomes the last used one. An occupied slot needs `overwrite` (@gdd-pending, §13.2). */
export function createSlot(save: SaveFile, slotId: string, now: string, options: { overwrite?: boolean } = {}): SaveFile {
  assertSlotId(slotId);
  if (save.slots[slotId] && !options.overwrite) throw new SaveOperationError(`slot ${slotId} is occupied`);
  return { ...save, lastSlotId: slotId, slots: { ...save.slots, [slotId]: emptySlot(now, save.settings.assistMode ? ASSIST.startLives : START_LIVES) } };
}

/** Opens a slot: it becomes the last used one. */
export function loadSlot(save: SaveFile, slotId: string): { save: SaveFile; slot: SaveSlot } {
  assertSlotId(slotId);
  const slot = save.slots[slotId];
  if (!slot) throw new SaveOperationError(`slot ${slotId} is empty`);
  return { save: { ...save, lastSlotId: slotId }, slot };
}

export function deleteSlot(save: SaveFile, slotId: string): SaveFile {
  assertSlotId(slotId);
  return {
    ...save,
    lastSlotId: save.lastSlotId === slotId ? null : save.lastSlotId,
    slots: { ...save.slots, [slotId]: null },
  };
}

export function canContinue(save: SaveFile): boolean {
  return save.lastSlotId !== null && save.slots[save.lastSlotId] !== null;
}

/** "Continuar": the last used slot, at its last active checkpoint (GDD §6.3). */
export function continueLast(save: SaveFile): { slotId: SlotId; slot: SaveSlot } {
  const slotId = save.lastSlotId;
  const slot = slotId ? save.slots[slotId] : null;
  if (!slotId || !slot) throw new SaveOperationError('no last slot');
  return { slotId, slot };
}
